import { computed, Injectable, signal } from '@angular/core';

/** Actions the drive surface registers so top-nav / breadcrumb can drive the embedded explorer. */
export interface DriveCompareController {
  startCompare(): void;
  exitCompare(): void;
}

/**
 * Mirror of the drive explorer's compare state for out-of-tree UI (top-nav compare
 * button, breadcrumb close chip). The explorer owns the state — only the drive
 * surface writes it via `syncState`; actions are delegated to the registered
 * controller so explorer-internal flips propagate automatically.
 */
@Injectable({ providedIn: 'root' })
export class DriveCompareUiService {
  private readonly _compareActive = signal(false);
  private readonly _canStartCompare = signal(false);
  private _controller: DriveCompareController | null = null;

  readonly compareActive = this._compareActive.asReadonly();
  /** Compare toggle is shown only when comparing or a compare can start (playground parity). */
  readonly showCompareToggle = computed(() => this._compareActive() || this._canStartCompare());

  public registerController(controller: DriveCompareController): void {
    this._controller = controller;
  }

  /** Identity-checked so a late destroy never clobbers a newer surface's registration. */
  public unregisterController(controller: DriveCompareController): void {
    if (this._controller === controller) {
      this._controller = null;
      this.syncState(false, false);
    }
  }

  /** Called only by the drive surface's mirror effect. */
  public syncState(compareActive: boolean, canStartCompare: boolean): void {
    this._compareActive.set(compareActive);
    this._canStartCompare.set(canStartCompare);
  }

  public toggleCompare(): void {
    if (this._compareActive()) {
      this._controller?.exitCompare();
    } else {
      this._controller?.startCompare();
    }
  }

  public exitCompare(): void {
    this._controller?.exitCompare();
  }
}
